import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {getAdminDocumentAPI, updateDocumentAPI} from '../../../services/admin';
import styles from './EditAdminDocumentInfo.module.css';
import {UpdateDocumentRequest} from "../../../types/admin";
import {DocumentType} from "../../../types/document";
import AdminLayout from "../layout/AdminLayout";
import {DocumentInfoResponseData} from "../../../types/client";
import FileInput from '../../../components/common/input/FileInput';
import {useAlert} from "../../common/error/AlertContext";
import Button from "../../common/button/Button";
//어드민 문서 수정 페이지
const EditAdminDocumentInfo: React.FC = () => {
    const navigate = useNavigate();
    const {id} = useParams<{ id: string }>();
    const [initialLoading, setInitialLoading] = useState(true);
    const [submitLoading, setSubmitLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        type: '' as DocumentType,
        content: '',
        deleteFile: false
    });
    const [file, setFile] = useState<File | null>(null);
    const [currentFileName, setCurrentFileName] = useState<string | undefined>();
    const {showAlert} = useAlert();

    useEffect(() => {
        const fetchDocument = async () => {
            try {
                if (!id) return;
                //어드민 문서 조회 API
                const response = await getAdminDocumentAPI(parseInt(id));
                const documentData = response as DocumentInfoResponseData;

                setFormData({
                    title: documentData.title,
                    type: documentData.documentType,
                    content: documentData.content,
                    deleteFile: false
                });
                setCurrentFileName(documentData.fileName);
            } catch (error) {
                console.error('문서 조회 실패:', error);
                showAlert('문서를 불러오는데 실패했습니다.', "error");
                navigate('/admin/documents');
            } finally {
                setInitialLoading(false);
            }
        };

        fetchDocument();
    }, [id, navigate]);

    //입력
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    // 첨부파일 수정
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files?.[0]) {
            setFile(e.target.files[0]);
        } else {
            setFile(null);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!id) return;
        try {
            // 필수 입력 검증
            if (!formData.title.trim()) {
                showAlert('제목을 입력해주세요.', "error");
                return;
            }
            if (!formData.content.trim()) {
                showAlert('내용을 입력해주세요.', "error");
                return;
            }
            setSubmitLoading(true);

            const updateRequest: UpdateDocumentRequest = {
                documentId: parseInt(id),
                title: formData.title.trim(),
                type: formData.type,
                content: formData.content.trim(),
                deleteFile: formData.deleteFile
            };

            // 문서 수정 API
            await updateDocumentAPI(updateRequest, file);

            showAlert('문서가 성공적으로 수정되었습니다.', "info");
            // 문서 테이블 페이지로 이동
            navigate(`/admin/documents/${id}`);
        } catch (error) {
            console.error('문서 수정 실패:', error);
        } finally {
            setSubmitLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <AdminLayout>
                <div className={styles.loadingContainer}>
                    문서를 불러오는 중...
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className={styles.container}>
                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.header}>
                        <h1 className={styles.title}>문서 수정</h1>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="title" className={styles.label}>제목</label>
                        {/*문서 제목 수정*/}
                        <input
                            type="text"
                            id="title"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            className={styles.input}
                            placeholder="제목을 입력하세요"
                            maxLength={100}
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="type" className={styles.label}>문서 종류</label>
                        {/*문서 종류 수정, 견적서, 가공가이드라인만 선택 가능*/}
                        <select
                            id="type"
                            name="type"
                            value={formData.type}
                            onChange={handleInputChange}
                            className={styles.select}
                            required
                            disabled={formData.type === DocumentType.보고서}
                        >
                            {(formData.type === DocumentType.보고서
                                    ? [DocumentType.보고서]
                                    : Object.values(DocumentType).filter(type => type !== DocumentType.보고서)  // 아닌 경우 보고서 제외
                            ).map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.formGroup}>
                        <label htmlFor="content" className={styles.label}>내용</label>
                        {/*문서 내용 수정*/}
                        <textarea
                            id="content"
                            name="content"
                            value={formData.content}
                            onChange={handleInputChange}
                            className={styles.textarea}
                            placeholder="내용을 입력하세요"
                            rows={10}
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        {/*파일 첨부파일 수정*/}
                        <FileInput
                            label="첨부파일"
                            onChange={handleFileChange}
                            value={file}
                            accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                        />
                        {currentFileName && !file && !formData.deleteFile && (
                            <div className={styles.currentFileWrapper}>
                                <span className={styles.currentFile}>
                                    현재 파일: {currentFileName}
                                </span>
                                {/*첨부파일 삭제 버튼*/}
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({...prev, deleteFile: true}))}
                                    className={styles.deleteFileButton}
                                    aria-label="파일 삭제"
                                >
                                    ✕
                                </button>
                            </div>
                        )}
                    </div>

                    <div className={styles.buttonGroup}>
                        {/*취소 버튼*/}
                        <Button
                            type="button"
                            onClick={() => navigate(`/admin/documents/${id}`)}
                            variant="secondary"
                        >
                            취소
                        </Button>
                        {/*수정 버튼*/}
                        <Button
                            type="submit"
                            disabled={submitLoading || !formData.title.trim() || !formData.content.trim()}
                        >
                            {submitLoading ? '수정 중...' : '수정'}
                        </Button>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
};

export default EditAdminDocumentInfo;